import { Box, Skeleton, Typography } from '@mui/material';
import { Procedure } from 'interfaces/procedure';
import { map, size, sum } from 'lodash';
import React from 'react';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { DEFAULT_PAGE_SIZE } from 'utils/useEncodedFilters/constants';
import { ExperimentResultsSelection } from 'utils/useEncodedFilters/types';

export const PageStats: React.FC<
  React.PropsWithChildren<{
    totalCases: number;
    totalSlides: number;
    slidesInPreviousPages: number;
    proceduresInPreviousPages: number;
    pageCases?: Procedure[];
    casesLoading?: boolean;
    pendingSlidesMode?: boolean;
  }>
> = ({
  totalCases,
  totalSlides,
  slidesInPreviousPages,
  proceduresInPreviousPages,
  pageCases,
  casesLoading,
  pendingSlidesMode,
}) => {
  const { queryParams } = useEncodedFilters({
    experimentResultsSelection: ExperimentResultsSelection.OnlyQAFailed,
  });

  const page = queryParams.page || 1;
  const pageSize = queryParams.pageSize || DEFAULT_PAGE_SIZE;

  let casesRangeEnd = 0;
  let casesRangeStart = 0;
  let slidesRangeEnd = 0;
  let slidesRangeStart = 0;
  let casesInPage = 0;
  let slidesInPage = 0;

  if (queryParams?.slidesMode) {
    slidesInPage = pageSize;
    slidesRangeStart = totalSlides > 0 ? pageSize * (page - 1) + 1 : 0;
    slidesRangeEnd = slidesRangeStart + slidesInPage >= totalSlides ? totalSlides : slidesRangeStart + slidesInPage - 1;

    casesInPage = size(pageCases);
    casesRangeStart =
      totalCases > 0 ? (proceduresInPreviousPages + 1 > totalCases ? totalCases : proceduresInPreviousPages + 1) : 0;
    casesRangeEnd = casesRangeStart + casesInPage >= totalCases ? totalCases : casesRangeStart + casesInPage - 1;
  } else {
    casesInPage = pageSize;
    casesRangeStart = totalCases > 0 ? pageSize * (page - 1) + 1 : 0;
    casesRangeEnd = casesRangeStart + casesInPage >= totalCases ? totalCases : casesRangeStart + casesInPage - 1;

    slidesInPage = sum(map(pageCases, (caseData) => size(caseData?.slides)));
    slidesRangeStart =
      totalSlides > 0 ? (slidesInPreviousPages + 1 > totalSlides ? totalSlides : slidesInPreviousPages + 1) : 0;
    slidesRangeEnd = slidesRangeStart + slidesInPage >= totalSlides ? totalSlides : slidesRangeStart + slidesInPage - 1;
  }

  const slidesCountTitle = !isNaN(Number(slidesInPreviousPages))
    ? `${slidesRangeStart} - ${slidesRangeEnd} slides of ${totalSlides} slides`
    : `${slidesInPage} slides of ${totalSlides} slides`;

  const casesCountTitle = !isNaN(Number(proceduresInPreviousPages))
    ? `${casesRangeStart} - ${casesRangeEnd} cases of ${totalCases} cases`
    : `${casesInPage} cases of ${totalCases} cases`;

  const baseTitle = queryParams?.slidesMode || pendingSlidesMode ? slidesCountTitle : casesCountTitle;
  const secondaryTitle = queryParams?.slidesMode ? casesCountTitle : slidesCountTitle;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        columnGap: 1,
        rowGap: 0.25,
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
      }}
    >
      {casesLoading ? (
        <>
          <Skeleton variant="text" animation="wave" width={120} sx={{ fontSize: '1.2em' }} />
          {!pendingSlidesMode && <Skeleton variant="text" animation="wave" width={80} />}
        </>
      ) : (
        <>
          <Typography variant="body2" data-cy="page-stats-title" sx={{ whiteSpace: 'nowrap' }}>
            {baseTitle}
          </Typography>
          {!pendingSlidesMode && (
            <Typography variant="caption" data-cy="page-stats-secondary-title" sx={{ whiteSpace: 'nowrap' }}>
              | {secondaryTitle}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};
